import { useEffect, useRef, useState } from "react"
import api from "../services/api"
import { socket } from "../socket"
import { useLogout } from "./Logout"
import {
  Settings,
  Bell,
  Menu,
  User,
  LogOut,
  X,
  CheckCheck,
  Trash2,
} from "lucide-react"
import { Link } from "react-router-dom"
import { auth } from "../auth/auth"

export default function Header({ onMenuClick }) {
  const logout = useLogout()
  const [open, setOpen] = useState(false)
  const [user, setUser] = useState(auth.getUser())
  const menuRef = useRef(null)

  // Notification modal state
  const [notifOpen, setNotifOpen] = useState(false)
  const notifDialogRef = useRef(null)

  // Settings modal state
  const [settingsOpen, setSettingsOpen] = useState(false)
  const settingsDialogRef = useRef(null)

  // Notifications data
  const [notifications, setNotifications] = useState([])
  const [loadingNotifs, setLoadingNotifs] = useState(false)

  const unreadCount = notifications.filter((n) => !n.read).length

  const fallbackAvatar =
    "data:image/svg+xml;utf8," +
    encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40">
      <rect width="100%" height="100%" fill="#e5e7eb"/>
      <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle"
        font-family="Arial" font-size="10" fill="#111827">User</text>
    </svg>
  `)

  // ----------------------------
  // Close dropdown on outside click
  // ----------------------------
  useEffect(() => {
    function onDocClick(e) {
      if (!menuRef.current) return
      if (!menuRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener("mousedown", onDocClick)
    return () => document.removeEventListener("mousedown", onDocClick)
  }, [])

  // ----------------------------
  // Close on ESC (dropdown + modals)
  // ----------------------------
  useEffect(() => {
    function onKeyDown(e) {
      if (e.key === "Escape") {
        setOpen(false)
        setNotifOpen(false)
        setSettingsOpen(false)
      }
    }
    document.addEventListener("keydown", onKeyDown)
    return () => document.removeEventListener("keydown", onKeyDown)
  }, [])

  // ----------------------------
  // Close notification modal on outside click
  // ----------------------------
  useEffect(() => {
    if (!notifOpen) return

    function onOutsideClick(e) {
      if (!notifDialogRef.current) return
      if (!notifDialogRef.current.contains(e.target)) setNotifOpen(false)
    }

    document.addEventListener("mousedown", onOutsideClick)
    return () => document.removeEventListener("mousedown", onOutsideClick)
  }, [notifOpen])

  // ----------------------------
  // Close settings modal on outside click
  // ----------------------------
  useEffect(() => {
    if (!settingsOpen) return

    function onOutsideClick(e) {
      if (!settingsDialogRef.current) return
      if (!settingsDialogRef.current.contains(e.target)) setSettingsOpen(false)
    }

    document.addEventListener("mousedown", onOutsideClick)
    return () => document.removeEventListener("mousedown", onOutsideClick)
  }, [settingsOpen])

  // ----------------------------
  // Optional: lock body scroll when any modal open
  // (fixed deps)
  // ----------------------------
  useEffect(() => {
    if (!settingsOpen && !notifOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = prev
    }
  }, [notifOpen, settingsOpen])

  // ----------------------------
  // Keep user state in sync with auth changes
  // ----------------------------
  useEffect(() => {
    const update = () => setUser(auth.getUser())
    update()

    window.addEventListener("auth:changed", update)
    window.addEventListener("storage", update)

    return () => {
      window.removeEventListener("auth:changed", update)
      window.removeEventListener("storage", update)
    }
  }, [])

  // ----------------------------
  // Persist notifications in localStorage (per-user)
  // so they don't disappear on reload
  // ----------------------------
  useEffect(() => {
    const uid = user?._id
    if (!uid) return
    const saved = localStorage.getItem(`notifications:${uid}`)
    if (saved) {
      try {
        setNotifications(JSON.parse(saved))
      } catch {
        // ignore bad cache
      }
    }
  }, [user?._id])

  useEffect(() => {
    const uid = user?._id
    if (!uid) return
    localStorage.setItem(`notifications:${uid}`, JSON.stringify(notifications))
  }, [notifications, user?._id])

  // ----------------------------
  // Socket: receive new notifications
  // ----------------------------
  useEffect(() => {
    if (!user?._id) return

    socket.emit("join", user._id)

    const handler = (notif) => {
      setNotifications((prev) => {
        if (prev.some((x) => x._id === notif._id)) return prev
        return [notif, ...prev]
      })
    }

    socket.off("notification:new")
    socket.on("notification:new", handler)

    return () => {
      socket.off("notification:new", handler)
    }
  }, [user?._id])

  // ----------------------------
  // Fetch notifications from API
  // (loading state prevents flash)
  // ----------------------------
  useEffect(() => {
    if (!user?._id) return

    const fetchNotifications = async () => {
      setLoadingNotifs(true)

      try {
        const res = await api.get(`/api/notifications/${user._id}`)

        setNotifications(res.data)
      } catch (err) {
        console.error("Failed to fetch notifications", err.response || err)
      } finally {
        setLoadingNotifs(false)
      }
    }

    fetchNotifications()
  }, [user?._id])

  // ----------------------------
  // Actions
  // ----------------------------
  async function markAllRead() {
    if (!user?._id) return

    try {
      await api.put(`/api/notifications/user/${user._id}/read-all`, null)

      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })))
    } catch (err) {
      console.error("Failed to mark notifications as read", err.response || err)
    }
  }

  function clearAll() {
    // optional: also delete on server if you have endpoint
    setNotifications([])
  }

  async function markOneRead(id) {
    try {
      await api.put(`/api/notifications/${id}/read`, null)

      setNotifications((prev) =>
        prev.map((n) => (n._id === id ? { ...n, read: true } : n))
      )
    } catch (err) {
      console.error("Failed to mark notification as read", err.response || err)
    }
  }

  return (
    <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b border-gray-200 bg-white px-4 sm:px-6">
      {/* Left: Mobile menu */}
      <button
        onClick={onMenuClick}
        className="md:hidden flex h-9 w-9 items-center justify-center rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200"
        aria-label="Open menu"
      >
        <Menu size={18} />
      </button>

      {/* Right: Actions */}
      <div className="flex items-center gap-3 sm:gap-4 ml-auto">
        <button
          onClick={() => setSettingsOpen(true)}
          className="flex h-9 w-9 items-center justify-center rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200"
        >
          <Settings size={22} />
        </button>

        {/* Bell + unread badge + modal trigger */}
        <button
          onClick={() => setNotifOpen(true)}
          className="relative flex h-9 w-9 items-center justify-center rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200"
          aria-label="Open notifications"
        >
          <Bell size={22} />
          {unreadCount > 0 && (
            <span className="absolute -right-1 -top-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-xs font-semibold text-white">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </button>

        {/* Avatar + dropdown */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setOpen((v) => !v)}
            className="rounded-full focus:outline-none focus:ring-2 focus:ring-gray-300"
            aria-haspopup="menu"
            aria-expanded={open}
          >
            <img
              src={user?.avatarUrl || fallbackAvatar}
              alt="User avatar"
              className="h-11 w-11 cursor-pointer rounded-full object-cover"
            />
          </button>

          {open && (
            <div
              className="absolute right-0 mt-2 w-44 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg"
              role="menu"
            >
              <Link
                to="/profile"
                className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                role="menuitem"
                onClick={() => setOpen(false)}
              >
                <User size={20} />
                Profile
              </Link>

              <button
                onClick={logout}
                className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                role="menuitem"
              >
                <LogOut size={20} />
                Logout
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Notification Modal */}
      {notifOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* overlay */}
          <div className="absolute inset-0 bg-black/40" />

          {/* dialog */}
          <div
            ref={notifDialogRef}
            role="dialog"
            aria-modal="true"
            aria-label="Notifications"
            className="relative z-10 w-[92vw] max-w-md rounded-2xl bg-white shadow-xl"
          >
            {/* header */}
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
              <div className="flex items-center gap-2">
                <span className="text-base font-semibold text-gray-900">
                  Notifications
                </span>
                {unreadCount > 0 && (
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                    {unreadCount} unread
                  </span>
                )}
              </div>

              <button
                onClick={() => setNotifOpen(false)}
                className="rounded-lg p-2 text-gray-600 hover:bg-gray-100"
                aria-label="Close notifications"
              >
                <X size={18} />
              </button>
            </div>

            {/* actions */}
            <div className="flex items-center gap-2 px-4 py-3">
              <button
                onClick={markAllRead}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <CheckCheck size={18} />
                Mark all read
              </button>

              <button
                onClick={clearAll}
                className="ml-auto inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <Trash2 size={18} />
                Clear
              </button>
            </div>

            {/* list */}
            <div className="max-h-[60vh] overflow-auto px-2 pb-3">
              {loadingNotifs ? (
                <div className="px-4 py-8 text-center text-sm text-gray-500">
                  Loading notifications...
                </div>
              ) : notifications.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-gray-500">
                  No notifications
                </div>
              ) : (
                <ul className="space-y-2">
                  {notifications.map((n) => (
                    <li key={n._id}>
                      <button
                        onClick={() => {
                          // mark read only when user clicks an item
                          if (!n.read) markOneRead(n._id)
                          // if you want to navigate to something, do it here
                        }}
                        className={`w-full rounded-xl border px-4 py-3 text-left hover:bg-gray-50 ${
                          n.read
                            ? "border-gray-200"
                            : "border-blue-200 bg-blue-50/40"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-900">
                                {n.title}
                              </span>
                              {!n.read && (
                                <span className="h-2 w-2 rounded-full bg-blue-500" />
                              )}
                            </div>
                            <p className="mt-1 text-sm text-gray-600">
                              {n.body}
                            </p>
                          </div>
                          <span className="whitespace-nowrap text-xs text-gray-500">
                            {new Date(n.createdAt).toLocaleString()}
                          </span>
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* footer */}
            <div className="border-t border-gray-200 px-4 py-3">
              <button
                onClick={() => setNotifOpen(false)}
                className="w-full rounded-xl bg-gray-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-gray-800"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {settingsOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" />
          <div
            ref={settingsDialogRef}
            role="dialog"
            aria-modal="true"
            aria-label="Settings"
            className="relative z-10 w-[92vw] max-w-md rounded-2xl bg-white shadow-xl"
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
              <span className="text-base font-semibold text-gray-900">
                Settings
              </span>
              <button
                onClick={() => setSettingsOpen(false)}
                className="rounded-lg p-2 text-gray-600 hover:bg-gray-100"
                aria-label="Close settings"
              >
                <X size={18} />
              </button>
            </div>

            {/* Settings content */}
            <div className="p-4 space-y-4">
              <div>
                <label htmlFor="theme" className="text-sm text-gray-700">
                  Theme
                </label>
                <select
                  id="theme"
                  className="w-full mt-1 p-2 border border-gray-300 rounded-md"
                >
                  <option>Light</option>
                  <option>Dark</option>
                </select>
              </div>

              <div>
                <label htmlFor="language" className="text-sm text-gray-700">
                  Language
                </label>
                <select
                  id="language"
                  className="w-full mt-1 p-2 border border-gray-300 rounded-md"
                >
                  <option>English</option>
                  <option>Spanish</option>
                  <option>French</option>
                </select>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 px-4 py-3">
              <button
                onClick={() => setSettingsOpen(false)}
                className="w-full rounded-xl bg-gray-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-gray-800"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  )
}
